import moment from 'moment'
import { socket } from './io/socket'
import { FUNNEL_ACTIONS } from '../hooks/useFunnelTracking'
import { getDeviceAndOSInfo } from './deviceDetection'

/**
 * Check if two dates are the same day
 * @param {string | Date} date1 - The first date
 * @param {string | Date} date2 - The second date
 * @returns {boolean} - True if the dates are the same day, false otherwise
 */
export const isSameDay = (date1, date2) => {
  return moment(date1).isSame(moment(date2), 'day')
}

/**
 * Track funnel events using socket.io
 * @param {string} actionType - The type of action to track
 * @param {number} organizationId - The organization ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const trackFunnelEvent = async (
  actionType,
  organizationId,
  location
) => {
  if (!socket.connected) {
    console.error('Socket not connected for funnel tracking')

    return { success: false, error: 'Socket not connected' }
  }

  return new Promise(resolve => {
    const timeoutId = setTimeout(() => {
      resolve({ success: false, error: 'Request timeout' })
    }, 5000)

    const deviceInfo = getDeviceAndOSInfo()

    socket.emit(
      'trackFunnelEvent',
      {
        actionType,
        organizationId,
        location,
        deviceInfo,
      },
      response => {
        clearTimeout(timeoutId)
        resolve(response)
      }
    )
  })
}

/**
 * Track when a component is added to the canvas (only once per day per organization)
 * @param {number} organizationId - The organization ID
 */
export const trackAddedComponent = async organizationId => {
  const key = `adalo_tracked_component_${organizationId}`
  const stored = localStorage.getItem(key)

  if (stored && isSameDay(stored, new Date())) {
    return { success: true, skipped: true }
  }

  localStorage.setItem(key, new Date().toISOString())

  return trackFunnelEvent(FUNNEL_ACTIONS.ADDED_COMPONENT, organizationId)
}

/**
 * Track when a screen is added (only once per day per organization)
 * @param {number} organizationId - The organization ID
 */
export const trackAddedScreen = async organizationId => {
  const key = `adalo_tracked_screen_${organizationId}`
  const stored = localStorage.getItem(key)

  if (stored && isSameDay(stored, new Date())) {
    return { success: true, skipped: true }
  }

  localStorage.setItem(key, new Date().toISOString())

  return trackFunnelEvent(FUNNEL_ACTIONS.ADDED_SCREEN, organizationId)
}

/**
 * Track when a collection is created (only once per day per organization)
 * @param {number} organizationId - The organization ID
 */
export const trackCreatedCollection = async organizationId => {
  const key = `adalo_tracked_collection_${organizationId}`
  const stored = localStorage.getItem(key)

  if (stored && isSameDay(stored, new Date())) {
    return { success: true, skipped: true }
  }

  localStorage.setItem(key, new Date().toISOString())

  return trackFunnelEvent(FUNNEL_ACTIONS.CREATED_COLLECTION, organizationId)
}

/**
 * Track when a maker clicks an "Upgrade" button (only once per day per organization)
 * @param {number} organizationId - The organization ID
 * @param {string} location - The location of the upgrade button
 */
export const trackClickedUpgradeNow = async (organizationId, location) => {
  const key = `adalo_tracked_upgrade_now_${organizationId}`
  const stored = localStorage.getItem(key)

  if (stored && isSameDay(stored, new Date())) {
    return { success: true, skipped: true }
  }

  localStorage.setItem(key, new Date().toISOString())

  return trackFunnelEvent(
    FUNNEL_ACTIONS.CLICKED_UPGRADE_NOW,
    organizationId,
    location
  )
}
